import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';
import AinaaEmblem from '@/components/AinaaEmblem';

const Navigation = ({ onWaitlistClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  const [portalLoggedIn, setPortalLoggedIn] = useState(false);
  const [adminLoggedIn, setAdminLoggedIn] = useState(false);

  useEffect(() => {
    setPortalLoggedIn(localStorage.getItem('ainaa_portal_logged_in') === 'true');
    setAdminLoggedIn(localStorage.getItem('ainaa_admin_logged_in') === 'true');
  }, [location.pathname]);

  const navItems = [
    { label: 'Home', href: '/', isRoute: true },
    { label: 'Registry', href: '/registry', isRoute: true },
    { label: 'Contact', href: '/contact', isRoute: true },
  ];

  const scrollToTop = () => {
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    requestAnimationFrame(() => {
      document.documentElement.style.scrollBehavior = '';
    });
  };

  const handleNav = (item) => {
    setIsMenuOpen(false);
    if (item.href === '/') {
      if (isHome) { scrollToTop(); } else { navigate('/'); }
      return;
    }
    if (item.isRoute) { navigate(item.href); return; }
    if (item.href.startsWith('#')) {
      if (isHome) {
        const el = document.querySelector(item.href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      } else { navigate('/' + item.href); }
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (isHome) { scrollToTop(); } else { navigate('/'); }
  };

  const renderCTA = (mobile = false) => {
    if (portalLoggedIn) {
      return (
        <Link to="/portal"
          className={`btn-ainaa-primary ${mobile ? 'px-6 py-3 text-sm' : 'px-5 py-2.5 text-[13px]'} font-semibold uppercase tracking-wider inline-flex items-center gap-2`}
          data-testid="nav-dashboard-btn" onClick={() => setIsMenuOpen(false)}>
          Dashboard <ArrowRight size={14} />
        </Link>
      );
    }
    if (adminLoggedIn) {
      return (
        <Link to="/admin"
          className={`btn-ainaa-primary ${mobile ? 'px-6 py-3 text-sm' : 'px-5 py-2.5 text-[13px]'} font-semibold uppercase tracking-wider inline-flex items-center gap-2`}
          data-testid="nav-console-btn" onClick={() => setIsMenuOpen(false)}>
          Console <ArrowRight size={14} />
        </Link>
      );
    }
    return (
      <Link to="/portal"
        className={`btn-ainaa-primary ${mobile ? 'px-6 py-3 text-sm' : 'px-5 py-2.5 text-[13px]'} font-semibold uppercase tracking-wider inline-flex items-center gap-2`}
        data-testid="nav-login-btn" onClick={() => setIsMenuOpen(false)}>
        Member Login <ArrowRight size={14} />
      </Link>
    );
  };

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="navigation"
    >
      <div className="glass px-6 md:px-12 py-3 flex items-center justify-between border-b-0" style={{ borderBottom: 'none' }}>
        <a href="/" onClick={handleLogoClick} className="flex items-center gap-3 flex-shrink-0" data-testid="nav-logo">
          <AinaaEmblem size={38} />
          <div className="flex flex-col leading-none">
            <span className="font-['Playfair_Display'] text-lg font-semibold tracking-tight text-[#0F1B2D]">AINAA</span>
            <span className="hidden sm:block text-[7px] uppercase tracking-[0.15em] text-[#9B9589] font-semibold leading-tight max-w-[200px]">
              Artist Identification Network &amp; Accreditation Archive
            </span>
          </div>
        </a>

        <div className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <button key={item.label} onClick={() => handleNav(item)}
              className="text-[13px] font-medium text-[#4A4640] hover:text-[#0F1B2D] transition-colors tracking-wide uppercase"
              data-testid={`nav-${item.label.toLowerCase()}`}>
              {item.label}
            </button>
          ))}
        </div>

        <div className="hidden md:block flex-shrink-0">{renderCTA()}</div>

        <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-[#0F1B2D] p-2" data-testid="mobile-menu-toggle">
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }} className="md:hidden glass mx-4 mt-2 rounded-lg p-4" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              {navItems.map((item) => (
                <button key={item.label} onClick={() => handleNav(item)}
                  className="text-left text-sm font-medium text-[#4A4640] hover:text-[#0F1B2D] transition-colors py-2 uppercase tracking-wide">
                  {item.label}
                </button>
              ))}
              <div className="mt-2">{renderCTA(true)}</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navigation;
